import {
  auth,
  db,
  storage,
} from "../../firebase/config";

import {
  collection,
  serverTimestamp,
  updateDoc,
  setDoc,
  doc,
  arrayUnion,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";

import {
  ref,
  uploadBytes,
} from "firebase/storage";

export const LOAD_PROJECTS = "LOAD_PROJECTS";
export const ADD_PROJECT_SUCCESS =
  "ADD_PROJECT_SUCCESS";
export const ADD_PROJECT_FAIL =
  "ADD_PROJECT_FAIL";
export const UPDATE_PROJECT_STATUS =
  "UPDATE_PROJECT_STATUS";

/* =========================
   HELPERS
========================= */

const normalizeEmail = (email) =>
  typeof email === "string"
    ? email.trim().toLowerCase()
    : "";

const createAuditUser = (profile = {}) => {
  const firebaseUser = auth.currentUser;

  return {
    uid: firebaseUser?.uid || "",
    email: normalizeEmail(firebaseUser?.email),
    name:
      profile.displayName ||
      [
        profile.firstName,
        profile.lastName,
      ]
        .filter(Boolean)
        .join(" ")
        .trim() ||
      firebaseUser?.displayName ||
      "",
  };
};

/* =========================
   LOAD PROJECTS
========================= */

export const listenToProjects =
  ({ email, isAdmin } = {}) =>
  (dispatch) => {
    if (!email) {
      dispatch({
        type: LOAD_PROJECTS,
        payload: [],
      });

      return () => {};
    }

    const projectsCollection = collection(
      db,
      "projects"
    );

    const convertSnapshotToMap = (snapshot) => {
      return new Map(
        snapshot.docs.map((projectDocument) => {
          const projectData =
            projectDocument.data();

          return [
            projectDocument.id,
            {
              id: projectDocument.id,
              ...projectData,
              projectNumber:
                projectData.projectNumber ||
                projectDocument.id.slice(0, 9),
            },
          ];
        })
      );
    };

    if (isAdmin) {
      return onSnapshot(
        projectsCollection,
        (snapshot) => {
          dispatch({
            type: LOAD_PROJECTS,
            payload: Array.from(
              convertSnapshotToMap(
                snapshot
              ).values()
            ),
          });
        },
        (error) => {
          console.error(
            "Administrator projects listener error:",
            error
          );
        }
      );
    }

    const normalizedEmail =
      normalizeEmail(email);

    const projectQueries = [
      query(
        projectsCollection,
        where(
          "createdBy.email",
          "==",
          normalizedEmail
        )
      ),
      query(
        projectsCollection,
        where(
          "contactEmail",
          "==",
          normalizedEmail
        )
      ),
      query(
        projectsCollection,
        where(
          "additionalEmails",
          "array-contains",
          normalizedEmail
        )
      ),
    ];

    const queryResults =
      projectQueries.map(() => new Map());

    const dispatchMergedProjects = () => {
      const mergedProjects = new Map();

      queryResults.forEach((resultMap) => {
        resultMap.forEach(
          (project, projectId) => {
            mergedProjects.set(
              projectId,
              project
            );
          }
        );
      });

      dispatch({
        type: LOAD_PROJECTS,
        payload: Array.from(
          mergedProjects.values()
        ),
      });
    };

    const unsubscribes =
      projectQueries.map(
        (projectQuery, queryIndex) =>
          onSnapshot(
            projectQuery,
            (snapshot) => {
              queryResults[queryIndex] =
                convertSnapshotToMap(
                  snapshot
                );

              dispatchMergedProjects();
            },
            (error) => {
              console.error(
                `Project listener ${
                  queryIndex + 1
                } failed:`,
                error
              );
            }
          )
      );

    return () => {
      unsubscribes.forEach(
        (unsubscribe) => {
          unsubscribe();
        }
      );
    };
  };

/* =========================
   UPDATE PROJECT
========================= */

export const updateProject =
  (projectId, data) => async () => {
    try {
      if (!projectId) {
        throw new Error(
          "A project ID is required."
        );
      }

      const projectRef = doc(
        db,
        "projects",
        projectId
      );

      const allowedUpdates = {
        projectTitle:
          data.projectTitle?.trim() || "",
        parish:
          data.parish?.trim() || "",
        projectDescription:
          data.projectDescription?.trim() || "",
        pastor:
          data.pastor?.trim() || "",
        contactName:
          data.contactName?.trim() || "",
        contactEmail:
          normalizeEmail(data.contactEmail),
        contactPhone:
          data.contactPhone?.trim() || "",
        updatedAt: serverTimestamp(),
      };

      await updateDoc(
        projectRef,
        allowedUpdates
      );
    } catch (error) {
      console.error(
        "Project update failed:",
        error
      );

      throw error;
    }
  };

/* =========================
   ARCHIVE PROJECT
========================= */

export const archiveProject =
  (projectId, profile = {}) => async () => {
    try {
      if (!projectId) {
        throw new Error(
          "A project ID is required."
        );
      }

      if (!auth.currentUser) {
        throw new Error(
          "You must be signed in."
        );
      }

      const projectRef = doc(
        db,
        "projects",
        projectId
      );

      await updateDoc(projectRef, {
        archived: true,
        archivedAt: serverTimestamp(),
        archivedBy:
          createAuditUser(profile),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error(
        "Project archive failed:",
        error
      );

      throw error;
    }
  };

/* =========================
   RESTORE PROJECT
========================= */

export const restoreProject =
  (projectId, profile = {}) => async () => {
    try {
      if (!projectId) {
        throw new Error(
          "A project ID is required."
        );
      }

      if (!auth.currentUser) {
        throw new Error(
          "You must be signed in."
        );
      }

      const projectRef = doc(
        db,
        "projects",
        projectId
      );

      await updateDoc(projectRef, {
        archived: false,
        restoredAt: serverTimestamp(),
        restoredBy:
          createAuditUser(profile),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error(
        "Project restore failed:",
        error
      );

      throw error;
    }
  };

/* =========================
   UPLOAD ATTACHMENT
========================= */

export const uploadProjectAttachment =
  async (projectId, file) => {
    try {
      if (!projectId || !file) {
        throw new Error(
          "A project and file are required."
        );
      }

      const extension = file.name.includes(".")
        ? file.name
            .split(".")
            .pop()
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "")
        : "";

      const fileId =
        typeof crypto !== "undefined" &&
        crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()
              .toString(36)
              .slice(2)}`;

      const storedFileName = extension
        ? `${fileId}.${extension}`
        : fileId;

      const filePath =
        `projects/${projectId}/attachments/` +
        storedFileName;

      const storageRef = ref(
        storage,
        filePath
      );

      await uploadBytes(
        storageRef,
        file,
        {
          contentType:
            file.type ||
            "application/octet-stream",
        }
      );

      const attachment = {
        name: file.name,
        storedFileName,
        path: filePath,
        contentType:
          file.type ||
          "application/octet-stream",
        size: file.size,
        createdAt:
          new Date().toISOString(),
      };

      await updateDoc(
        doc(db, "projects", projectId),
        {
          attachments:
            arrayUnion(attachment),
          updatedAt: serverTimestamp(),
        }
      );

      return attachment;
    } catch (error) {
      console.error(
        "Project attachment upload failed:",
        error
      );

      throw error;
    }
  };

/* =========================
   ADD PROJECT
========================= */

export const addProject =
  (data) => async (dispatch) => {
    try {
      const projectRef = doc(
        collection(db, "projects")
      );

      const projectNumber =
        projectRef.id.slice(0, 9);

      const projectData = {
        ...data,
        projectNumber,
        status: "Open",
        archived: false,
        createdAt: serverTimestamp(),
      };

      await setDoc(
        projectRef,
        projectData
      );

      dispatch({
        type: ADD_PROJECT_SUCCESS,
        payload: {
          id: projectRef.id,
          ...data,
          projectNumber,
          status: "Open",
          archived: false,
        },
      });

      return projectRef;
    } catch (error) {
      dispatch({
        type: ADD_PROJECT_FAIL,
        payload: error.message,
      });

      throw error;
    }
  };

/* =========================
   UPDATE STATUS
========================= */

export const updateProjectStatus =
  (projectId, status) =>
  async (dispatch) => {
    try {
      if (!projectId) {
        throw new Error(
          "A project ID is required."
        );
      }

      const projectRef = doc(
        db,
        "projects",
        projectId
      );

      await updateDoc(projectRef, {
        status,
        updatedAt: serverTimestamp(),
      });

      dispatch({
        type: UPDATE_PROJECT_STATUS,
        payload: {
          projectId,
          status,
        },
      });
    } catch (error) {
      console.error(
        "Project status update failed:",
        error
      );

      throw error;
    }
  };
/* =========================
   UPDATE PRIORITY
========================= */

const VALID_PROJECT_PRIORITIES = [
  "Unassigned",
  "Low",
  "Normal",
  "High",
  "Urgent",
];

export const updateProjectPriority =
  (projectId, priority) => async () => {
    try {
      if (!projectId) {
        throw new Error(
          "A project ID is required."
        );
      }

      if (
        !VALID_PROJECT_PRIORITIES.includes(
          priority
        )
      ) {
        throw new Error(
          "Please select a valid project priority."
        );
      }

      await updateDoc(
        doc(db, "projects", projectId),
        {
          priority,
          updatedAt: serverTimestamp(),
        }
      );
    } catch (error) {
      console.error(
        "Project priority update failed:",
        error
      );

      throw error;
    }
  };
